import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Calendar, DateRangePicker } from 'react-date-range';
import MaskedInput from 'react-text-mask';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './reactDateRangeAdjacentDays.css';
import cx from 'classnames';
import Popover from '../Popover/Popover';
import InlinePicker from '../Popover/InlinePicker';
import { Button } from '../Button';
import { getFormattedDate } from '@shared/lib/localize';
import './DateField.css';

const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((label, value) => ({ value, label }));

const buildYearOptions = (focusedDate, minDate, maxDate) => {
  const min = minDate ? minDate.getFullYear() : focusedDate.getFullYear() - 100;
  const max = maxDate ? maxDate.getFullYear() : focusedDate.getFullYear() + 20;
  const options = [];
  for (let y = max; y >= min; y -= 1) options.push({ value: y, label: String(y) });
  return options;
};

/**
 * _NavHeader — replaces react-date-range's default month / year
 * `<select>` elements with our `<InlinePicker>` so the calendar
 * navigation matches the rest of the design system. Wired through
 * `<Calendar navigatorRenderer={...}>` and the equivalent prop on
 * `<DateRangePicker>` (forwards to its inner `<Calendar>`).
 *
 * `changeShownDate` API (from react-date-range Calendar source):
 *   - changeShownDate(monthIndex, 'setMonth')
 *   - changeShownDate(year, 'setYear')
 *   - changeShownDate(±1, 'monthOffset')
 */
const _NavHeader = ({ focusedDate, changeShownDate, minDate, maxDate }) => {
  const [monthOpen, setMonthOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);

  const yearOptions = useMemo(
    () => buildYearOptions(focusedDate, minDate, maxDate),
    [focusedDate, minDate, maxDate]
  );

  return (
    <div className="qhs-date-field__nav">
      <button
        type="button"
        className="qhs-date-field__nav-arrow"
        onClick={() => changeShownDate(-1, 'monthOffset')}
        aria-label="Previous month"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div className="qhs-date-field__nav-pickers">
        <InlinePicker
          open={monthOpen}
          onClose={() => setMonthOpen(false)}
          value={focusedDate.getMonth()}
          options={MONTH_OPTIONS}
          onChange={m => changeShownDate(m, 'setMonth')}
          trigger={
            <button
              type="button"
              className="qhs-date-field__nav-picker"
              onClick={() => setMonthOpen(o => !o)}
            >
              {MONTH_OPTIONS[focusedDate.getMonth()].label}
            </button>
          }
        />
        <InlinePicker
          open={yearOpen}
          onClose={() => setYearOpen(false)}
          value={focusedDate.getFullYear()}
          options={yearOptions}
          onChange={y => changeShownDate(y, 'setYear')}
          trigger={
            <button
              type="button"
              className="qhs-date-field__nav-picker"
              onClick={() => setYearOpen(o => !o)}
            >
              {focusedDate.getFullYear()}
            </button>
          }
        />
      </div>
      <button
        type="button"
        className="qhs-date-field__nav-arrow"
        onClick={() => changeShownDate(+1, 'monthOffset')}
        aria-label="Next month"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );
};

const CalendarSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const CloseSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DEFAULT_PLACEHOLDER_SINGLE = 'MM/DD/YYYY';
const DEFAULT_PLACEHOLDER_RANGE = 'MM/DD/YYYY - MM/DD/YYYY';

// MM/DD/YYYY typing mask for the single-mode text input. `guide={false}`
// keeps the slashes auto-inserting without showing placeholder chars.
const DATE_MASK = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];

const dayStart = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Parse a fully-typed MM/DD/YYYY string into a real Date, rejecting
// impossible dates (13/45, 02/30, ...) by round-tripping the parts.
const parseTypedDate = str => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((str || '').trim());
  if (!m) return null;
  const mm = Number(m[1]);
  const dd = Number(m[2]);
  const yyyy = Number(m[3]);
  const d = new Date(yyyy, mm - 1, dd);
  if (d.getMonth() !== mm - 1 || d.getDate() !== dd || d.getFullYear() !== yyyy) {
    return null;
  }
  return d;
};

// Initial selection always seeds with non-null Date objects — the
// range picker rejects null endpoints, so an empty filter falls
// back to "today / today" until the user picks a real range.
const buildSelection = parsed => ({
  startDate: parsed?.startDate || new Date(),
  endDate: parsed?.endDate || new Date(),
  key: 'selection',
});

/**
 * QHS DateField
 * -------------
 * Date picker shaped like an Input with a right-side calendar icon.
 * Opens a Popover hosting react-date-range's `Calendar` (single mode)
 * or `DateRangePicker` (range mode), themed via --qhs-* tokens.
 *
 * Single mode (default, Tier 2):
 *
 *   <DateField
 *     value={values.dateOfBirth}                 // 'MM/DD/YYYY' or ''
 *     onChange={(s) => setFieldValue('dateOfBirth', s)}
 *     maxDate={new Date()}
 *   />
 *
 *   Click a day -> applies -> closes. Matches design-system pop-date
 *   "single date selection" pattern.
 *
 * Range mode (Tier 3):
 *
 *   <DateField
 *     range
 *     value={filterValue}                        // 'MM/DD/YYYY,MM/DD/YYYY' or ''
 *     onChange={setFilter}
 *   />
 *
 *   Calendar shows preset sidebar (Last 7 / 30 days, This month, …).
 *   User picks start, picks end — selection is buffered as `draft`.
 *   Apply commits, Cancel / Escape / outside-click discards, Clear
 *   wipes the range. Mirrors the population-health DateRangePopover
 *   so range filters behave identically across the app.
 *
 * The caller deals only in formatted strings — single dates are
 * `MM/DD/YYYY`; ranges are `start,end` joined by a comma.
 */
const DateField = ({
  value,
  onChange,
  range = false,
  size = 'md',
  highlighted = false,
  maxDate,
  minDate,
  placeholder,
  fullWidth = false,
  disabled = false,
  error,
  className,
  ...rest
}) => {
  const { btn = {} } = useSelector(s => s.i18n) || {};
  const [open, setOpen] = useState(false);

  // Local buffer for the single-mode typable input. Kept in sync with
  // the committed `value` so calendar picks, form resets and external
  // changes all flow into the text box; in-progress typing lives here
  // until it parses into a valid date (or is discarded on blur).
  const [text, setText] = useState(value || '');
  useEffect(() => {
    if (!range) setText(value || '');
  }, [value, range]);

  const withinBounds = d => {
    if (!d) return false;
    if (maxDate && dayStart(d) > dayStart(maxDate)) return false;
    if (minDate && dayStart(d) < dayStart(minDate)) return false;
    return true;
  };

  const effectivePlaceholder =
    placeholder || (range ? DEFAULT_PLACEHOLDER_RANGE : DEFAULT_PLACEHOLDER_SINGLE);

  // Parse the formatted-string value back into Date instances.
  const parsed = useMemo(() => {
    if (!value) {
      return range ? { startDate: null, endDate: null } : null;
    }
    if (range) {
      const [startStr, endStr] = value.split(',');
      return {
        startDate: startStr ? new Date(startStr) : null,
        endDate: endStr ? new Date(endStr) : null,
      };
    }
    return new Date(value);
  }, [value, range]);

  // Range-mode draft state — buffered so the table doesn't refresh
  // on every click while the user is mid-selection. Re-seeded on
  // open so external changes (e.g. clear-all) flow through.
  const [draft, setDraft] = useState(() => buildSelection(parsed));
  // `parsed` deliberately excluded from deps — re-seeding on every
  // parent render would clobber the user's in-progress selection.
  // Open + range are the only triggers.
  useEffect(() => {
    if (open && range) setDraft(buildSelection(parsed));
  }, [open, range]); // eslint-disable-line

  // Display string in the trigger button.
  const displayValue = useMemo(() => {
    if (!value) return effectivePlaceholder;
    if (range) {
      const [start, end] = value.split(',');
      return `${start || ''} - ${end || ''}`;
    }
    return value;
  }, [value, range, effectivePlaceholder]);

  const handleSingleChange = date => {
    const formatted = date ? getFormattedDate(date) : '';
    onChange(formatted);
    setText(formatted);
    setOpen(false);
  };

  // Single-mode text typing: mirror keystrokes into `text`, and commit
  // to `onChange` as soon as the buffer parses into a valid, in-bounds
  // date. Clearing the field commits '' immediately.
  const handleTextChange = e => {
    const raw = e.target.value;
    setText(raw);
    if (raw.trim() === '') {
      onChange('');
      return;
    }
    const d = parseTypedDate(raw);
    if (d && withinBounds(d)) onChange(getFormattedDate(d));
  };

  // Discard an incomplete/invalid partial on blur by snapping the text
  // back to the last committed value.
  const handleTextBlur = () => {
    const d = parseTypedDate(text);
    if (text.trim() !== '' && !(d && withinBounds(d))) setText(value || '');
  };

  const handleTextKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const d = parseTypedDate(text);
      if (d && withinBounds(d)) {
        onChange(getFormattedDate(d));
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setText(value || '');
      setOpen(false);
    }
  };

  const handleApplyRange = () => {
    if (!draft.startDate || !draft.endDate) {
      onChange('');
    } else {
      onChange(`${getFormattedDate(draft.startDate)},${getFormattedDate(draft.endDate)}`);
    }
    setOpen(false);
  };

  const handleClearRange = () => {
    onChange('');
    setOpen(false);
  };

  const handleCancelRange = () => {
    setOpen(false);
  };

  const handleClear = e => {
    // Stop propagation so the surrounding trigger button's onClick
    // doesn't fire and re-open the popover. Close it explicitly so
    // the calendar collapses if it happened to be open.
    e.stopPropagation();
    onChange('');
    setText('');
    setOpen(false);
  };

  const showClear = !!value && !disabled;

  // Range mode keeps the button trigger: the whole control opens the
  // buffered range picker (there is no single value to type).
  const rangeTrigger = (
    <button
      type="button"
      onClick={() => !disabled && setOpen(v => !v)}
      disabled={disabled}
      className={cx(
        'qhs-date-field',
        `qhs-date-field--${size}`,
        {
          'qhs-date-field--full': fullWidth,
          'qhs-date-field--invalid': !!error,
          'qhs-date-field--placeholder': !value,
          'qhs-date-field--highlighted': highlighted && !!value,
        },
        className
      )}
      {...rest}
    >
      <span className="qhs-date-field__value">{displayValue}</span>
      <span className="qhs-date-field__icon">
        {showClear ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear date"
            className="qhs-date-field__clear"
            onClick={handleClear}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClear(e);
              }
            }}
          >
            {CloseSvg}
          </span>
        ) : (
          CalendarSvg
        )}
      </span>
    </button>
  );

  // Single mode is a typable masked input (MM/DD/YYYY) plus a calendar
  // button that opens the picker. Typing commits a valid date directly;
  // the calendar remains available for pointer users.
  const singleTrigger = (
    <div
      className={cx(
        'qhs-date-field',
        'qhs-date-field--input',
        `qhs-date-field--${size}`,
        {
          'qhs-date-field--full': fullWidth,
          'qhs-date-field--invalid': !!error,
          'qhs-date-field--highlighted': highlighted && !!value,
        },
        className
      )}
      {...rest}
    >
      <MaskedInput
        mask={DATE_MASK}
        guide={false}
        value={text}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        onKeyDown={handleTextKeyDown}
        disabled={disabled}
        placeholder={effectivePlaceholder}
        className="qhs-date-field__text"
        inputMode="numeric"
        aria-invalid={!!error || undefined}
      />
      {/* One-click reset for a filled single date — parity with the
          range variant, whose icon slot turns into a clear control.
          Unlike range, the calendar opener stays: it is the only
          picker affordance in this variant. tabIndex -1 keeps the
          filter row's form-like Tab order (keyboard users clear by
          deleting the text). */}
      {showClear && (
        <button
          type="button"
          className="qhs-date-field__clear-btn"
          onClick={handleClear}
          aria-label="Clear date"
          tabIndex={-1}
        >
          {CloseSvg}
        </button>
      )}
      <button
        type="button"
        className="qhs-date-field__cal-btn"
        onClick={() => !disabled && setOpen(v => !v)}
        disabled={disabled}
        aria-label="Open calendar"
        tabIndex={-1}
      >
        {CalendarSvg}
      </button>
    </div>
  );

  const trigger = range ? rangeTrigger : singleTrigger;

  return (
    <>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        trigger={trigger}
        placement="bottom-start"
      >
        <div className="qhs-date-field__panel">
          {range ? (
            <>
              <DateRangePicker
                preventSnapRefocus
                ranges={[draft]}
                onChange={item => setDraft(item.selection)}
                moveRangeOnFirstSelection={false}
                maxDate={maxDate}
                minDate={minDate}
                months={1}
                direction="vertical"
                showSelectionPreview={false}
                showDateDisplay={false}
                rangeColors={['#2E48D3']}
                navigatorRenderer={(focusedDate, changeShownDate) => (
                  <_NavHeader
                    focusedDate={focusedDate}
                    changeShownDate={changeShownDate}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                )}
              />
              <div className="qhs-date-field__foot">
                <Button variant="ghost" size="sm" onClick={handleClearRange}>
                  {btn.clearFilter || 'Clear'}
                </Button>
                <div className="qhs-date-field__foot-actions">
                  <Button variant="ghost" size="sm" onClick={handleCancelRange}>
                    {btn.cancel || 'Cancel'}
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleApplyRange}>
                    {btn.apply || 'Apply'}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <Calendar
              preventSnapRefocus
              date={parsed || new Date()}
              onChange={handleSingleChange}
              maxDate={maxDate}
              minDate={minDate}
              color="#2E48D3"
              navigatorRenderer={(focusedDate, changeShownDate) => (
                <_NavHeader
                  focusedDate={focusedDate}
                  changeShownDate={changeShownDate}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              )}
            />
          )}
        </div>
      </Popover>
      {error && <p className="qhs-date-field__error">{error}</p>}
    </>
  );
};

export default DateField;
